import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { sentenceCase } from "change-case";
import {
  formatFechaDisplay,
  formatFechaParaBackend,
} from "../utils/formatTime";

// Material UI
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Grid,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  IconButton,
  createTheme,
  ThemeProvider,
} from "@material-ui/core";
import {
  Delete as DeleteIcon,
  Attachment as AttachmentIcon,
  Assignment as AssignmentIcon,
} from "@material-ui/icons";
import * as locales from "@material-ui/core/locale";

// Componentes propios
import Page from "../components/Page";
import Label from "../components/Label";
import Scrollbar from "../components/Scrollbar";
import { TareasListHead } from "../components/_dashboard/tareas";
import { Titulo } from "src/components/Titulo";
import ModifyTaskStateModal from "../components/_dashboard/tareas/ModifyTaskStateModal";
import { Alerta } from "../components/Alerta";
import SimpleModal from "../layouts/SimpleModal";
import ArchivosAdjuntosDialog from "../components/tareas/ArchivosAdjuntosDialog";
import SubTareasDialog from "../components/tareas/SubTareasDialog";
import ExportToExcelSubTareas from "src/components/ExportToExcelSubTareas";
import TabsComponente from "src/components/TabsComponente";
import SolicitarCambioEstadoDialog from "src/components/tareas/SolicitarCambioEstadoDialog";
import ExportToExcelTareas from "src/components/ExportToExcelTareas";
import CantidadRemuneracionesDialog from "src/components/tareas/CantidadRemuneracionesDialog";
import { useSnackbar } from "src/components/snackbar";

// Nuevo sistema de filtros
import useTareasFilters from "src/components/tareas/hooks/useTareasFilters";
import TareasFilterPanel from "src/components/tareas/filters/TareasFilterPanel";

// Servicios y utilidades
import { dbx } from "../utils/urlBack_End";
import { formatDate } from "../utils/converter";
import {
  tareasApi,
  useGetClientesDistintosPorSucursalQuery,
  useGetEmpleadosDistintosPorSucursalQuery,
} from "../services/tareasApi";
import { empleadosApi } from "../services/empleadosApi";
import { sucursalesApi } from "../services/sucursalesApi";
import { AlertaModalV2 } from "src/components/AlertaSwall";

// Estilos centralizados
import {
  URGENCY_COLORS,
  ESTADO_COLORS,
  getDiasTranscurridosBadgeStyle,
} from "src/components/tareas/styles/tareasStyles.js";
import { makeStyles } from "@material-ui/styles";

// ==================== CONFIGURACIÓN ====================

const LOCALE = "esES";
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

const ESTADOS_TAREAS_ACTIVAS = [
  "PENDIENTE",
  "EN PROCESO",
  "OBSERVADA SII",
  "PENDIENTE DE FACTURACION",
];
const ESTADOS_TAREAS_TERMINADAS = [
  "TERMINADA",
  "FACTURADO",
  "CANCELADO O RECHAZADO",
];

const ESTADOS_TAREA_MODAL = [
  "PENDIENTE",
  "EN PROCESO",
  "OBSERVADA SII",
  "TERMINADA",
  "FACTURADO",
  "CANCELADO O RECHAZADO",
];

// ==================== ESTILOS CON makeStyles ====================

const useStyles = makeStyles((theme) => ({
  // Card contenedor
  card: {
    borderRadius: 12,
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    border: "1px solid #E0E0E0",
    overflow: "hidden",
  },
  cardHeader: {
    padding: "12px 16px",
    borderBottom: "1px solid #F5F5F5",
    backgroundColor: "#FFFFFF",
  },
  // Tabla
  tableContainer: {
    "& .MuiTableCell-head": {
      backgroundColor: "#FAFAFA",
      fontWeight: 600,
      fontSize: "0.813rem",
      color: "#424242",
      borderBottom: "2px solid #E0E0E0",
      whiteSpace: "nowrap",
      padding: "14px 16px",
    },
  },
  tableRow: {
    height: 60,
    transition: "background-color 0.15s ease",
    position: "relative",
    "&:hover": {
      backgroundColor: "#FAFAFA",
    },
  },
  tableRowVencida: {
    borderLeft: `4px solid ${URGENCY_COLORS.vencida}`,
  },
  tableRowAdvertencia: {
    borderLeft: `4px solid ${URGENCY_COLORS.advertencia}`,
  },
  tableCell: {
    fontSize: "0.875rem",
    color: "#212121",
    borderBottom: "1px solid #F5F5F5",
    padding: "12px 16px",
  },
  tableCellClickable: {
    cursor: "pointer",
    "&:hover": {
      color: "#1976D2",
    },
  },
  // Chips de estado
  estadoChip: {
    fontWeight: 500,
    fontSize: "0.75rem",
    height: 28,
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      transform: "scale(1.02)",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    },
  },
  estadoDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    marginRight: 6,
    display: "inline-block",
  },
  // Botones
  exportButton: {
    borderColor: "#BDBDBD",
    color: "#616161",
    fontWeight: 500,
    textTransform: "none",
    fontSize: "0.875rem",
    "&:hover": {
      borderColor: "#9E9E9E",
      backgroundColor: "#FAFAFA",
    },
  },
  deleteButton: {
    color: "#9E9E9E",
    transition: "all 0.2s ease",
    "&:hover": {
      color: "#EF5350",
      backgroundColor: "#FFEBEE",
    },
  },
  attachmentButton: {
    color: "#9E9E9E",
    transition: "all 0.2s ease",
    "&:hover": {
      color: "#1976D2",
      backgroundColor: "#E3F2FD",
    },
  },
  subtareasButton: {
    color: "#1976D2",
    fontSize: "0.813rem",
    fontWeight: 500,
    textTransform: "none",
    padding: "4px 12px",
    minWidth: "auto",
    borderRadius: 16,
    backgroundColor: "#E3F2FD",
    "&:hover": {
      backgroundColor: "#BBDEFB",
    },
  },
  // Badge días transcurridos
  diasBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 10px",
    borderRadius: 12,
    fontSize: "0.813rem",
    fontWeight: 500,
  },
  // Contador de tareas
  taskCount: {
    fontSize: "0.875rem",
    color: "#616161",
    fontWeight: 500,
  },
  // Empty state
  emptyState: {
    padding: theme.spacing(6),
    textAlign: "center",
    color: "#9E9E9E",
  },
}));

// ==================== HELPERS ====================

const getToday = () => {
  const today = new Date();
  const day = today.getDate();
  const month = today.getMonth() + 1;
  const year = today.getUTCFullYear();
  return formatDate(`${year}/${month}/${day}`);
};

// ==================== COMPONENTE PRINCIPAL ====================

export default function Tareas() {
  const classes = useStyles();
  const navigate = useNavigate();
  const isMounted = useRef(true);
  const { enqueueSnackbar } = useSnackbar();

  // Auth state
  const {
    empleado: user,
    sucursal: sucursalObtenida,
    rol: rolObtenido,
  } = useSelector((state) => state.auth.user);

  const isAdmin = ["SUPER_ADMIN", "ADMIN", "SUPERVISOR"].includes(rolObtenido);

  // ==================== ESTADO LOCAL ====================

  // Paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const saved = localStorage.getItem("rowPerPageTask");
    return saved ? parseInt(saved, 10) : 10;
  });

  // Ordenamiento
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("fechaInicio");
  // eslint-disable-next-line no-unused-vars
  const [selected, setSelected] = useState([]);

  // Estado pestaña activa (0 = activas, 1 = terminadas)
  const [tabActiva, setTabActiva] = useState(0);

  // Modales y UI
  const [showModifyTaskState, setShowModifyTaskState] = useState(false);
  const [showCantidadRemuneraciones, setShowCantidadRemuneraciones] =
    useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showAttachments, setShowAttachments] = useState(false);
  const [specificTask, setSpecificTask] = useState(null);
  const [mostrarSubTareas, setMostrarSubTareas] = useState(false);
  const [idSubTarea, setIdSubtarea] = useState(null);
  const [mostrarSolicitarCambioEstado, setMostrarSolicitarCambioEstado] =
    useState(false);
  const [tareaSeleccionada, setTareaSeleccionada] = useState(null);

  // Loading y errores
  const [loadingUpdateId, setLoadingUpdateId] = useState(null);

  // Alertas
  const [error, setError] = useState("");
  const [showError, setShowError] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [color, setColor] = useState("success");

  // ==================== FILTROS  ====================

  const {
    filtros,
    filtrosActivos,
    hayFiltrosActivos,
    updateFiltro,
    toggleEstado,
    limpiarFiltros,
    removerFiltro,
    isAdmin: isAdminFilter,
  } = useTareasFilters([], { rolObtenido, storageKey: "tareasFilters" });

  // Determinar qué estados mostrar según la pestaña activa
  const estadosParaQuery = useMemo(() => {
    // Si hay filtros de estado aplicados manualmente, usarlos
    if (filtros.estados && filtros.estados.length > 0) {
      return filtros.estados.join(",");
    }

    // Si no, usar estados según la pestaña activa
    if (tabActiva === 0) {
      // Pestaña "Tareas Activas"
      return ESTADOS_TAREAS_ACTIVAS.join(",");
    } else {
      // Pestaña "Tareas Terminadas"
      return ESTADOS_TAREAS_TERMINADAS.join(",");
    }
  }, [filtros.estados, tabActiva]);

  // ==================== RTK QUERY ====================

  // Sucursales del empleado — necesarias para resolver "todas" -> lista explícita
  const { data: sucursalesEmpleado = [] } =
    sucursalesApi.useGetSucursalesEmpleadosQuery(user.rut);

  // Cuando el array está vacío significa "todas"; se resuelve a la lista completa de sucursales del empleado.
  const sucursalesParaQuery = useMemo(
    () =>
      filtros.sucursales.length > 0
        ? filtros.sucursales
        : sucursalesEmpleado.map((s) => s.refSucursal),
    [filtros.sucursales, sucursalesEmpleado],
  );

  const {
    data: tareasResponse,
    isLoading: loadingTable,
    refetch: recargarTareas,
    isFetching,
  } = tareasApi.useGetTareasAllQuery({
    id: sucursalObtenida.refSucursal,
    refEmpleado: user.rut,
    rolObtenido: rolObtenido,
    // Paginación
    page: page,
    limit: rowsPerPage,
    orderBy: orderBy,
    order: order,
    // Filtros
    busqueda: filtros.busqueda,
    estados: estadosParaQuery,
    tipoTarea: filtros.tipoTarea,
    fechaInicioDesde: formatFechaParaBackend(filtros.fechaInicioDesde),
    fechaInicioHasta: formatFechaParaBackend(filtros.fechaInicioHasta),
    fechaTerminoDesde: formatFechaParaBackend(filtros.fechaTerminoDesde),
    fechaTerminoHasta: formatFechaParaBackend(filtros.fechaTerminoHasta),
    cliente: filtros.cliente?.value || null,
    empleado: filtros.empleado?.value || null,
    sucursales: sucursalesParaQuery,
    soloEncargado: filtros.soloEncargado,
  });

  // Queries para opciones de filtros
  const { data: clientesDistintos = [] } =
    useGetClientesDistintosPorSucursalQuery({
      sucursal: sucursalObtenida.refSucursal,
      refEmpleado: user.rut,
      rolObtenido: rolObtenido,
      sucursales: sucursalesParaQuery,
    });

  const { data: empleadosDistintos = [] } =
    useGetEmpleadosDistintosPorSucursalQuery(
      {
        sucursal: sucursalObtenida.refSucursal,
        sucursales: sucursalesParaQuery,
      },
      { skip: !isAdmin },
    );

  // Extraer datos y metadata
  const tareasData = useMemo(
    () => tareasResponse?.data ?? [],
    [tareasResponse?.data],
  );
  const paginationMeta = tareasResponse?.pagination || {
    total: 0,
    page: 0,
    limit: 10,
    totalPages: 0,
  };

  // Opciones de filtros desde endpoints dedicados
  const opcionesClientes = clientesDistintos;
  const opcionesEmpleados = isAdmin ? empleadosDistintos : [];

  const [actualizarEstadoTarea] = tareasApi.useUpdateCambiaEstadoMutation();
  const [eliminarTareaMutation] = tareasApi.useDeleteTareaMutation();
  const [addHistorialAcciones] = empleadosApi.useAddHistorialAccionesMutation();

  // ==================== EFECTOS ====================

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Historial de acciones
  useEffect(() => {
    const urlActual = window.location.href;
    if (!urlActual.includes("http://localhost:3000")) {
      addHistorialAcciones({
        user: user.rut,
        body: { urlAccion: urlActual },
      })
        .unwrap()
        .catch(() => {});
    }
  }, [addHistorialAcciones, user.rut]);

  // Reset página cuando cambian los filtros
  useEffect(() => {
    setPage(0);
  }, [filtros]);

  // ==================== HEADERS DE TABLA ====================

  const tableHeadActivas = useMemo(() => {
    const headers = [
      { id: "nombreCliente", label: "Cliente", alignRight: false },
      { id: "nombre", label: "Tarea", alignRight: false },
      { id: "tipoTarea", label: "Tipo", alignRight: false },
      { id: "sucursal", label: "Sucursal", alignRight: false },
      { id: "fechaInicio", label: "Inicio", alignRight: false },
      { id: "fechaTermino", label: "Término", alignRight: false },
      { id: "diasLimites", label: "Días Límite", alignRight: false },
      { id: "diasTranscurridos", label: "Transcurridos", alignRight: false },
      { id: "estado", label: "Estado", alignRight: false },
      { id: "subtareas", label: "", alignRight: false },
      { id: "acciones", label: "", alignRight: false },
    ];

    if (isAdmin) {
      headers.unshift({
        id: "nombreEmpleado",
        label: "Empleado",
        alignRight: false,
      });
    }

    return headers;
  }, [isAdmin]);

  const tableHeadTerminadas = useMemo(() => {
    const headers = [
      { id: "nombreCliente", label: "Cliente", alignRight: false },
      { id: "nombre", label: "Tarea", alignRight: false },
      { id: "tipoTarea", label: "Tipo", alignRight: false },
      { id: "sucursal", label: "Sucursal", alignRight: false },
      { id: "fechaInicio", label: "Inicio", alignRight: false },
      { id: "fechaTermino", label: "Término", alignRight: false },
      { id: "estado", label: "Estado", alignRight: false },
    ];

    if (isAdmin) {
      headers.unshift({
        id: "nombreEmpleado",
        label: "Empleado",
        alignRight: false,
      });
    }

    return headers;
  }, [isAdmin]);

  // ==================== HANDLERS ====================

  const handleRequestSort = (_, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    setPage(0); // Reset página al cambiar ordenamiento
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      setSelected(tareasData.map((n) => n.id));
      return;
    }
    setSelected([]);
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const value = parseInt(event.target.value, 10);
    localStorage.setItem("rowPerPageTask", value);
    setRowsPerPage(value);
    setPage(0);
  };

  const handleTabChange = (_, newValue) => {
    setTabActiva(newValue);
    setPage(0);
  };

  const handleDelete = useCallback(
    (id) => {
      AlertaModalV2({
        title: "¿Eliminar la tarea?",
        text: "Se eliminará la tarea, ¿Está seguro?",
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
        icon: "warning",
      })
        .fire()
        .then((result) => {
          if (result.isConfirmed) {
            eliminarTareaMutation(id)
              .unwrap()
              .then(async () => {
                enqueueSnackbar("Tarea eliminada correctamente.", {
                  variant: "success",
                });
                try {
                  await dbx.filesDeleteV2({ path: `/Tareas/${id}` });
                } catch {}
                recargarTareas();
                setSelectedTask(null);
              })
              .catch(() => {
                enqueueSnackbar(
                  "No fue posible eliminar la tarea. Inténtelo nuevamente más tarde.",
                  {
                    variant: "error",
                  },
                );
              });
          }
        });
    },
    [eliminarTareaMutation, recargarTareas],
  );

  const handleChangeTaskState = useCallback(
    (id) => {
      const task = tareasData.find((t) => t.id === id);
      setSelectedTask(task);
      setShowModifyTaskState(true);
    },
    [tareasData],
  );

  const updateTaskInDB = useCallback(
    async (modificacionEstado) => {
      try {
        await actualizarEstadoTarea({
          id: modificacionEstado.id,
          body: {
            estado: modificacionEstado.estado,
            fechaTermino: modificacionEstado.fechaTermino,
          },
        }).unwrap();
        enqueueSnackbar("Estado tarea actualizado correctamente", {
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
        });
        setSelectedTask(null);
      } catch (error) {
        console.error("updateTaskInDB", error);
        enqueueSnackbar(
          error?.data?.message || "Error al actualizar el estado",
          {
            variant: "error",
            anchorOrigin: { vertical: "bottom", horizontal: "right" },
          },
        );
        setSelectedTask(null);
      } finally {
        setLoadingUpdateId(null);
      }
    },
    [actualizarEstadoTarea, enqueueSnackbar],
  );

  const doChangeTaskState = useCallback(
    (newState) => {
      if (!selectedTask) return;
      setLoadingUpdateId(selectedTask.id);

      if (selectedTask.estado === newState) {
        setShowModifyTaskState(false);
        setLoadingUpdateId(null);
        return;
      }

      let modificacionEstado = { ...selectedTask, estado: newState };

      // Caso especial: liquidaciones
      if (
        newState === "TERMINADA" &&
        (selectedTask.tipoTarea === "MES" ||
          selectedTask.tipoTarea === "AÑO") &&
        String(selectedTask.nombre).trim() === "LIQUIDACIONES"
      ) {
        setSelectedTask(modificacionEstado);
        setShowModifyTaskState(false);
        setShowCantidadRemuneraciones(true);
        return;
      }

      // Caso especial: tarea especial terminada
      if (newState === "TERMINADA") {
        if (selectedTask.tipoTarea === "ESPECIAL") {
          modificacionEstado.estado = "PENDIENTE DE FACTURACION";
        }
        modificacionEstado.fechaTermino = getToday();
      } else {
        modificacionEstado.fechaTermino = "";
      }

      setShowModifyTaskState(false);
      updateTaskInDB(modificacionEstado);
    },
    [selectedTask, updateTaskInDB],
  );

  const updateTaskInDBRemuneraciones = useCallback(
    (modificacionEstado) => {
      return actualizarEstadoTarea({
        id: modificacionEstado.id,
        body: {
          estado: modificacionEstado.estado,
          fechaTermino: modificacionEstado.fechaTermino,
          cantidadRemuneraciones: modificacionEstado.cantidadFactura,
        },
      })
        .unwrap()
        .then(() => {
          recargarTareas();
        });
    },
    [actualizarEstadoTarea, recargarTareas],
  );

  const handleAttachment = (tarea) => {
    setSpecificTask(tarea);
    setShowAttachments(true);
  };

  const handleOpenSubtareas = (tarea) => {
    setIdSubtarea(tarea);
    setMostrarSubTareas(true);
  };

  const handleSolicitarCambioEstado = (tarea) => {
    setTareaSeleccionada(tarea);
    setMostrarSolicitarCambioEstado(true);
  };

  // ==================== RENDER HELPERS ====================

  /**
   * Obtiene la clase CSS para la fila según urgencia
   */
  const getRowClassName = (diasLimites, diasTranscurridos) => {
    let rowClass = classes.tableRow;
    if (diasLimites === "0") {
      rowClass += ` ${classes.tableRowVencida}`;
    } else if (diasLimites === "" && diasTranscurridos >= 14) {
      rowClass += ` ${classes.tableRowAdvertencia}`;
    }
    return rowClass;
  };

  /**
   * Renderiza el chip de estado con estilo compacto
   */
  const renderEstadoLabel = (estado, onClick) => {
    const colors =
      ESTADO_COLORS[estado] || ESTADO_COLORS["CANCELADO O RECHAZADO"];

    return (
      <Box
        onClick={onClick}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          padding: "4px 12px",
          borderRadius: "14px",
          backgroundColor: colors.bg,
          cursor: "pointer",
          transition: "all 0.2s ease",
          "&:hover": {
            transform: "scale(1.02)",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          },
        }}
      >
        <Box
          component="span"
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: colors.dot,
            marginRight: "6px",
          }}
        />
        <Typography
          variant="caption"
          sx={{
            color: colors.text,
            fontWeight: 500,
            fontSize: "0.75rem",
          }}
        >
          {sentenceCase(estado)}
        </Typography>
      </Box>
    );
  };

  /**
   * Renderiza el badge de días transcurridos
   */
  const renderDiasTranscurridos = (dias) => {
    const badgeStyle = getDiasTranscurridosBadgeStyle(dias);
    return (
      <Box sx={badgeStyle}>
        {dias} {dias === 1 ? "día" : "días"}
      </Box>
    );
  };

  const navigateToTask = (id) => {
    navigate(`/dashboard/tareas/${id}`);
  };

  // ==================== RENDER FILAS ====================

  const renderTareaActivaRow = (tarea) => {
    const {
      id,
      nombre,
      tipoTarea,
      nombreSucursal,
      nombreCliente,
      nombreEmpleado,
      fechaInicio,
      fechaTermino,
      estado,
      diasTranscurridos,
      diasLimites,
    } = tarea;

    const isLoading = loadingUpdateId === id;

    if (isLoading) {
      return (
        <TableRow key={id} className={classes.tableRow}>
          <TableCell colSpan={isAdmin ? 12 : 11} align="center">
            <CircularProgress size={24} />
          </TableCell>
        </TableRow>
      );
    }

    return (
      <TableRow
        hover
        key={id}
        className={getRowClassName(diasLimites, diasTranscurridos)}
      >
        {isAdmin && (
          <TableCell
            className={`${classes.tableCell} ${classes.tableCellClickable}`}
            onClick={() => navigateToTask(id)}
          >
            <Typography variant="body2" noWrap>
              {nombreEmpleado}
            </Typography>
          </TableCell>
        )}
        <TableCell
          className={`${classes.tableCell} ${classes.tableCellClickable}`}
          onClick={() => navigateToTask(id)}
        >
          <Typography
            variant="body2"
            noWrap
            sx={{ maxWidth: 200, fontWeight: 500 }}
          >
            {nombreCliente}
          </Typography>
        </TableCell>
        <TableCell
          className={`${classes.tableCell} ${classes.tableCellClickable}`}
          onClick={() => navigateToTask(id)}
        >
          <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
            {nombre}
          </Typography>
        </TableCell>
        <TableCell
          className={`${classes.tableCell} ${classes.tableCellClickable}`}
          onClick={() => navigateToTask(id)}
        >
          <Label
            variant="ghost"
            color={
              tipoTarea === "ESPECIAL"
                ? "info"
                : tipoTarea === "MES"
                  ? "primary"
                  : "default"
            }
            sx={{ fontSize: "0.75rem" }}
          >
            {tipoTarea}
          </Label>
        </TableCell>
        <TableCell
          className={`${classes.tableCell} ${classes.tableCellClickable}`}
          onClick={() => navigateToTask(id)}
        >
          <Typography variant="body2" noWrap color="textSecondary">
            {nombreSucursal || "—"}
          </Typography>
        </TableCell>
        <TableCell
          className={`${classes.tableCell} ${classes.tableCellClickable}`}
          onClick={() => navigateToTask(id)}
        >
          {formatFechaDisplay(fechaInicio)}
        </TableCell>
        <TableCell
          className={`${classes.tableCell} ${classes.tableCellClickable}`}
          onClick={() => navigateToTask(id)}
        >
          {formatFechaDisplay(fechaTermino)}
        </TableCell>
        <TableCell className={classes.tableCell}>
          {diasLimites ? (
            <Typography variant="body2" color="textSecondary">
              {diasLimites}
            </Typography>
          ) : (
            <Typography variant="body2" color="textSecondary">
              —
            </Typography>
          )}
        </TableCell>
        <TableCell className={classes.tableCell}>
          {renderDiasTranscurridos(diasTranscurridos)}
        </TableCell>
        <TableCell className={classes.tableCell}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {renderEstadoLabel(estado, () => handleChangeTaskState(id))}
            {!["TERMINADA", "PENDIENTE DE FACTURACION", "FACTURADO"].includes(
              estado,
            ) && (
              <Tooltip title="Adjuntar archivos">
                <IconButton
                  size="small"
                  className={classes.attachmentButton}
                  onClick={() => handleAttachment(tarea)}
                >
                  <AttachmentIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </TableCell>
        <TableCell className={classes.tableCell}>
          <Button
            size="small"
            className={classes.subtareasButton}
            startIcon={<AssignmentIcon fontSize="small" />}
            onClick={() => handleOpenSubtareas(tarea)}
          >
            Subtareas
          </Button>
        </TableCell>
        <TableCell className={classes.tableCell} align="right">
          {["SUPER_ADMIN", "ADMIN"].includes(rolObtenido) &&
            !["TERMINADA", "PENDIENTE DE FACTURACION", "FACTURADO"].includes(
              estado,
            ) && (
              <Tooltip title="Eliminar tarea">
                <IconButton
                  size="small"
                  className={classes.deleteButton}
                  onClick={() => handleDelete(id)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
        </TableCell>
      </TableRow>
    );
  };

  const renderTareaTerminadaRow = (tarea) => {
    const {
      id,
      nombre,
      tipoTarea,
      nombreSucursal,
      nombreCliente,
      nombreEmpleado,
      fechaInicio,
      fechaTermino,
      estado,
    } = tarea;

    const handleEstadoClick = () => {
      if (["SUPER_ADMIN", "ADMIN"].includes(rolObtenido)) {
        handleChangeTaskState(id);
      } else {
        handleSolicitarCambioEstado(tarea);
      }
    };

    return (
      <TableRow hover key={id} className={classes.tableRow}>
        {isAdmin && (
          <TableCell
            className={`${classes.tableCell} ${classes.tableCellClickable}`}
            onClick={() => navigateToTask(id)}
          >
            <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
              {nombreEmpleado}
            </Typography>
          </TableCell>
        )}
        <TableCell
          className={`${classes.tableCell} ${classes.tableCellClickable}`}
          onClick={() => navigateToTask(id)}
        >
          <Typography
            variant="body2"
            noWrap
            sx={{ maxWidth: 200, fontWeight: 500 }}
          >
            {nombreCliente}
          </Typography>
        </TableCell>
        <TableCell
          className={`${classes.tableCell} ${classes.tableCellClickable}`}
          onClick={() => navigateToTask(id)}
        >
          <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
            {nombre}
          </Typography>
        </TableCell>
        <TableCell
          className={`${classes.tableCell} ${classes.tableCellClickable}`}
          onClick={() => navigateToTask(id)}
        >
          <Label
            variant="ghost"
            color={
              tipoTarea === "ESPECIAL"
                ? "info"
                : tipoTarea === "MES"
                  ? "primary"
                  : "default"
            }
            sx={{ fontSize: "0.75rem" }}
          >
            {tipoTarea}
          </Label>
        </TableCell>
        <TableCell
          className={`${classes.tableCell} ${classes.tableCellClickable}`}
          onClick={() => navigateToTask(id)}
        >
          <Typography variant="body2" noWrap color="textSecondary">
            {nombreSucursal || "—"}
          </Typography>
        </TableCell>
        <TableCell
          className={`${classes.tableCell} ${classes.tableCellClickable}`}
          onClick={() => navigateToTask(id)}
        >
          {formatFechaDisplay(fechaInicio)}
        </TableCell>
        <TableCell
          className={`${classes.tableCell} ${classes.tableCellClickable}`}
          onClick={() => navigateToTask(id)}
        >
          {formatFechaDisplay(fechaTermino)}
        </TableCell>
        <TableCell className={classes.tableCell}>
          {renderEstadoLabel(estado, handleEstadoClick)}
        </TableCell>
      </TableRow>
    );
  };

  // ==================== RENDER TABLAS ====================

  const renderTablaActivas = () => {
    return (
      <Card className={classes.card}>
        {/* Header de la card */}
        <Box className={classes.cardHeader}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography className={classes.taskCount}>
                {paginationMeta.total} tarea
                {paginationMeta.total !== 1 ? "s" : ""} activa
                {paginationMeta.total !== 1 ? "s" : ""}
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <ExportToExcelTareas
                filename="tareas_activas"
                sheetName="Tareas Activas"
                sucursalId={sucursalObtenida.refSucursal}
                refEmpleado={user.rut}
                rolObtenido={rolObtenido}
                filtros={filtros}
                estadosParaQuery={estadosParaQuery}
                sucursales={sucursalesParaQuery}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <ExportToExcelSubTareas
                filename="subtareas"
                sheetName="Subtareas"
                sucursalId={sucursalObtenida.refSucursal}
                empleadoRut={user.rut}
                rolObtenido={rolObtenido}
              />
            </Grid>
          </Grid>
        </Box>

        <Scrollbar>
          <TableContainer
            className={classes.tableContainer}
            sx={{ minWidth: 800 }}
          >
            {loadingTable || isFetching ? (
              <Box sx={{ p: 2 }}>
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={400}
                  sx={{ borderRadius: 1 }}
                />
              </Box>
            ) : (
              <Table>
                <TareasListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={tableHeadActivas}
                  rowCount={paginationMeta.total}
                  onRequestSort={handleRequestSort}
                  onSelectAllClick={handleSelectAllClick}
                />
                <TableBody>{tareasData.map(renderTareaActivaRow)}</TableBody>
                {tareasData.length === 0 && (
                  <TableBody>
                    <TableRow>
                      <TableCell
                        align="center"
                        colSpan={isAdmin ? 12 : 11}
                        className={classes.emptyState}
                      >
                        {hayFiltrosActivos ? (
                          <Box>
                            <Typography variant="body1" gutterBottom>
                              No se encontraron tareas con los filtros aplicados
                            </Typography>
                            <Button
                              size="small"
                              onClick={limpiarFiltros}
                              sx={{ mt: 1 }}
                            >
                              Limpiar filtros
                            </Button>
                          </Box>
                        ) : (
                          <Typography variant="body1">
                            No hay tareas activas que mostrar
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            )}
          </TableContainer>
        </Scrollbar>

        <ThemeProvider
          theme={(outerTheme) => createTheme(outerTheme, locales[LOCALE])}
        >
          <TablePagination
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            component="div"
            count={paginationMeta.total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: "1px solid #F5F5F5",
              ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
                {
                  fontSize: "0.875rem",
                },
            }}
          />
        </ThemeProvider>
      </Card>
    );
  };

  const renderTablaTerminadas = () => {
    return (
      <Card className={classes.card}>
        {/* Header de la card */}
        <Box className={classes.cardHeader}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography className={classes.taskCount}>
                {paginationMeta.total} tarea
                {paginationMeta.total !== 1 ? "s" : ""} terminada
                {paginationMeta.total !== 1 ? "s" : ""}
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <ExportToExcelTareas
                filename="tareas_terminadas"
                sheetName="Tareas Terminadas"
                sucursalId={sucursalObtenida.refSucursal}
                refEmpleado={user.rut}
                rolObtenido={rolObtenido}
                filtros={filtros}
                estadosParaQuery={estadosParaQuery}
                sucursales={sucursalesParaQuery}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <ExportToExcelSubTareas
                filename="subtareas"
                sheetName="SubTareas"
                setShowAlert={setShowAlert}
                setColor={setColor}
                setMessage={setError}
                sucursalId={sucursalObtenida.refSucursal}
                empleadoRut={user.rut}
                rolObtenido={rolObtenido}
              />
            </Grid>
          </Grid>
        </Box>

        <Scrollbar>
          <TableContainer
            className={classes.tableContainer}
            sx={{ minWidth: 800 }}
          >
            {loadingTable || isFetching ? (
              <Box sx={{ p: 3 }}>
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={400}
                  sx={{ borderRadius: 1 }}
                />
              </Box>
            ) : (
              <Table>
                <TareasListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={tableHeadTerminadas}
                  rowCount={paginationMeta.total}
                  onRequestSort={handleRequestSort}
                  onSelectAllClick={handleSelectAllClick}
                />
                <TableBody>{tareasData.map(renderTareaTerminadaRow)}</TableBody>
                {tareasData.length === 0 && (
                  <TableBody>
                    <TableRow>
                      <TableCell
                        align="center"
                        colSpan={isAdmin ? 8 : 7}
                        className={classes.emptyState}
                      >
                        {hayFiltrosActivos ? (
                          <Box>
                            <Typography variant="body1" gutterBottom>
                              No se encontraron tareas con los filtros aplicados
                            </Typography>
                            <Button
                              size="small"
                              onClick={limpiarFiltros}
                              sx={{ mt: 1 }}
                            >
                              Limpiar filtros
                            </Button>
                          </Box>
                        ) : (
                          <Typography variant="body1">
                            No hay tareas terminadas que mostrar
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            )}
          </TableContainer>
        </Scrollbar>

        <ThemeProvider
          theme={(outerTheme) => createTheme(outerTheme, locales[LOCALE])}
        >
          <TablePagination
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            component="div"
            count={paginationMeta.total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: "1px solid #F5F5F5",
              ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
                {
                  fontSize: "0.875rem",
                },
            }}
          />
        </ThemeProvider>
      </Card>
    );
  };

  // ==================== RENDER PRINCIPAL ====================

  return (
    <Page title="TAXTIC">
      <Container maxWidth={false} sx={{ px: { xs: 2, md: 3 } }}>
        <Titulo>
          <Typography variant="h4" gutterBottom>
            Tareas
          </Typography>
        </Titulo>

        {/* Panel de filtros */}
        <TareasFilterPanel
          filtros={filtros}
          updateFiltro={updateFiltro}
          toggleEstado={toggleEstado}
          limpiarFiltros={limpiarFiltros}
          removerFiltro={removerFiltro}
          filtrosActivos={filtrosActivos}
          hayFiltrosActivos={hayFiltrosActivos}
          opcionesClientes={opcionesClientes}
          opcionesEmpleados={opcionesEmpleados}
          isAdmin={isAdminFilter}
          rutEmpleado={user.rut}
          // Habilita el switch "Solo donde soy encargado", oculto por defecto
          // en el panel para que el tablero de inicio y la ficha de empleado
          // (que reutilizan el mismo panel) no lo hereden sin decidirlo.
          showFilters={{
            busqueda: true,
            sucursales: true,
            estados: true,
            tipoTarea: true,
            cliente: true,
            empleado: true,
            fechas: true,
            soloEncargado: true,
          }}
        />

        {/* Tabs de tareas */}
        <TabsComponente
          encabezados={["Tareas Activas", "Tareas Terminadas"]}
          tabs={[renderTablaActivas(), renderTablaTerminadas()]}
          value={tabActiva}
          onChange={handleTabChange}
        />

        {/* ==================== MODALES ==================== */}

        {/* Modal cambiar estado */}
        {selectedTask && (
          <ModifyTaskStateModal
            task={selectedTask}
            onSubmit={doChangeTaskState}
            onClose={() => setShowModifyTaskState(false)}
            states={ESTADOS_TAREA_MODAL}
            open={showModifyTaskState}
            cargandoDatos={false}
          />
        )}

        {/* Modal cantidad remuneraciones */}
        {selectedTask && showCantidadRemuneraciones && (
          <CantidadRemuneracionesDialog
            open={showCantidadRemuneraciones}
            onClose={() => {
              setShowCantidadRemuneraciones(false);
              setLoadingUpdateId(null);
              setSelectedTask(null);
            }}
            tarea={selectedTask}
            onGuardar={updateTaskInDBRemuneraciones}
          />
        )}

        {/* Modal archivos adjuntos */}
        {specificTask && (
          <SimpleModal
            maxWidth="md"
            open={showAttachments}
            showAcceptButton={false}
            showCancelButton={false}
            title="Archivos Adjuntos"
          >
            <ArchivosAdjuntosDialog
              tarea={specificTask}
              setShowError={setShowError}
              setError={(msg) => {
                setError(msg);
                setShowError(true);
              }}
              onClose={() => setShowAttachments(false)}
            />
          </SimpleModal>
        )}

        {/* Modal subtareas */}
        {mostrarSubTareas && (
          <SubTareasDialog
            open={mostrarSubTareas}
            setOpen={setMostrarSubTareas}
            tarea={idSubTarea}
          />
        )}

        {/* Modal solicitar cambio de estado */}
        {mostrarSolicitarCambioEstado && (
          <SolicitarCambioEstadoDialog
            open={mostrarSolicitarCambioEstado}
            setOpen={setMostrarSolicitarCambioEstado}
            tarea={tareaSeleccionada}
          />
        )}

        {/* ==================== ALERTAS ==================== */}

        <Alerta
          showAlert={showError}
          setShowAlert={setShowError}
          message={error}
          color="error"
        />

        {showAlert && (
          <Alerta
            showAlert={showAlert}
            setShowAlert={setShowAlert}
            message={error}
            color={color}
          />
        )}
      </Container>
    </Page>
  );
}
